import { useState } from "react";
import { Alert, Button, InputText, SectionCard } from "@/deps/ui-library";
import { useSaveTaxNames } from "@/api/queries";
import { useCan, useCurrentSession } from "@/app/session";

/**
 * What this organisation calls its tax registration and its taxes. The pack gives the names and they
 * can be changed here (ADR-0036). They are wording only, so no figure changes when they do.
 */
export function TaxNamesCard() {
  const { session } = useCurrentSession();
  const can = useCan();
  const editable = can("settings.tax.manage");
  const save = useSaveTaxNames();
  const [registration, setRegistration] = useState(session.taxNames.registration);
  const [valueAdded, setValueAdded] = useState(session.taxNames.valueAdded);
  const [withholding, setWithholding] = useState(session.taxNames.withholding);
  const pack = session.taxNames.packName;

  return (
    <SectionCard title="Tax names">
      <div className="flex flex-col gap-3">
        <p className="text-fg-muted text-sm">
          {pack ? `These come from the ${pack} pack. ` : ""}Rename them to match how your
          organisation says them. Rates and figures don't change. Leave a name empty to go back to
          the pack's.
        </p>
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <div className="grid gap-3 md:grid-cols-3">
          <InputText
            label="Tax registration number"
            maxLength={40}
            disabled={!editable}
            value={registration}
            onChange={(event) => setRegistration(event.target.value)}
          />
          <InputText
            label="Value added tax"
            maxLength={40}
            disabled={!editable}
            value={valueAdded}
            onChange={(event) => setValueAdded(event.target.value)}
          />
          <InputText
            label="Tax withheld at source"
            maxLength={40}
            disabled={!editable}
            value={withholding}
            onChange={(event) => setWithholding(event.target.value)}
          />
        </div>
        {editable ? (
          <div>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  { registration, valueAdded, withholding },
                  {
                    onSuccess: (names) => {
                      setRegistration(names.registration);
                      setValueAdded(names.valueAdded);
                      setWithholding(names.withholding);
                    },
                  },
                )
              }
            >
              {save.isPending ? "Saving" : "Save names"}
            </Button>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
